import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setCurrentVessel } from '../../store/vesselOverviewSlice';
import shipIcon from "../../assets/icons/icon-vessel-grey.png";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, Typography, Box, Link, TableSortLabel
} from '@mui/material';
import { downloadVesselFiles } from '../../services/fleetService';

const getScoreRingColor = (score) => {
  if (score >= 75) return "#00ab2e"; // green
  if (score >= 45) return "#029597"; // teal
  return "#a96401"; // orange
};

const getReceivedColor = (dateString) => {
  const receivedDate = new Date(dateString);
  const today = new Date();
  const diffDays = Math.floor((today - receivedDate) / (1000 * 60 * 60 * 24));

  if (diffDays <= 1) return "success.main";
  if (diffDays <= 2) return "success.light";
  if (diffDays <= 3) return "warning.main";
  if (diffDays <= 5) return "warning.main";
  return "error.main";
};

const getDynamicStatus = (receivedDateString) => {
  let status = 'Not Received';
  if (receivedDateString) {
    const receivedDate = new Date(receivedDateString);
    const today = new Date();

    const receivedDay = receivedDate.toISOString().split('T')[0];
    const todayDay = today.toISOString().split('T')[0];

    if (receivedDay === todayDay) {
      status = 'Received';
    } else {
      const diffDays = Math.floor((today - receivedDate) / (1000 * 60 * 60 * 24));
      if (diffDays <= 5) {
        status = 'Received';
      }
    }
  }

  const commonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    position: 'relative',
    right: '10px',
  };

  const circleStyle = (color) => ({
    width: 20,
    height: 20,
    borderRadius: '50%',
    backgroundColor: color,
  });

  switch (status) {
    case 'Received':
      return (
        <div style={commonStyle} title="Received">
          <span style={circleStyle('#00ab2e')}></span>
        </div>
      );
    case 'Not Received':
      return (
        <div style={commonStyle} title="Not Received">
          <span style={circleStyle('red')}></span>
        </div>
      );
    case 'Received with Error':
      return (
        <div style={commonStyle} title="Received with Error">
          <span style={circleStyle('#a96401')}></span>
        </div>
      );
    default:
      return null;
  }
};

const ColouredScore = ({ score, width = 60, height = 60 }) => {
  const radius = width / 2; // make radius dynamic based on width
  const stroke = 2;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const ringColor = getScoreRingColor(score);
  const textColor = getScoreRingColor(score);

  return (
    <svg height={height} width={width} viewBox={`0 0 ${width} ${height}`}>
      {/* Background Circle */}
      {/* <circle
        stroke="#eee"
        fill="transparent"
        strokeWidth={stroke}
        r={normalizedRadius}
        cx={width / 2}
        cy={height / 2}
      /> */}

      {/* Progress Circle */}
      {/* <circle
        stroke={ringColor}
        fill="transparent"
        strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        r={normalizedRadius}
        cx={width / 2}
        cy={height / 2}
      /> */}

      {/* Score Text */}
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dy=".3em"
        fontSize="16" // dynamic font size
        fontWeight="bold"
        fill={textColor}
      >
        {score}
      </text>
    </svg>
  );
};

const headCells = [
  { id: 'name', label: 'Vessels' },

  { id: 'emission', label: 'Emission Score' },
  { id: 'performance', label: 'Operational Score' },
    { id: 'score', label: 'Total Score' },
  { id: 'fleetRank', label: 'Fleet Rank' },
  // { id: 'machinery', label: 'Machinery' },
  { id: 'type', label: 'Type' },
  { id: 'received', label: 'Data received on' },
  { id: 'status', label: 'Data Quality' },
  { id: 'attachments', label: 'Attachments' },
];

const SortIcon = ({ direction, active }) => {
  const activeColor = "#1a325d";
  const inactiveColor = "#ffffff88";

  const arrowWidth = 4;
  const arrowHeight = 4;

  return (
    <Box display="flex" flexDirection="column" alignItems="center" ml={0.5}>
      <Box
        component="span"
        sx={{
          width: 10,
          height: 0,
          borderLeft: `${arrowWidth}px solid transparent`,
          borderRight: `${arrowWidth}px solid transparent`,
          borderBottom: `${arrowHeight}px solid ${active && direction === 'asc' ? activeColor : inactiveColor}`,
          mb: '2px',
        }}
      />
      <Box
        component="span"
        sx={{
          width: 10,
          height: 0,
          borderLeft: `${arrowWidth}px solid transparent`,
          borderRight: `${arrowWidth}px solid transparent`,
          borderTop: `${arrowHeight}px solid ${active && direction === 'desc' ? activeColor : inactiveColor}`,
        }}
      />
    </Box>
  );
};

const OverviewTable = ({ vessels = [] }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [openAttachments, setOpenAttachments] = useState(false);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [currentVesselName, setCurrentVesselName] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [search, setSearch] = useState('');

  const getStatusFromPerformance = (performance) => {
    if (!performance) return 'Not Received';
    const receivedDate = new Date(performance.createdAt);
    const now = new Date();
    const diffDays = Math.floor((now - receivedDate) / (1000 * 60 * 60 * 24));

    if (diffDays > 3) return 'Received with Error';
    return 'Received';
  };

  const transformedVessels = useMemo(() => {
    return vessels.map(vessel => {
      return {
        id: vessel.vesselId,
        name: vessel.vesselName,
        score: vessel.performance?.scores?.total ?? 0,
        emission: vessel.performance?.scores?.emission ?? 0,
        performance: vessel.performance?.scores?.operation ?? 0,
        fleetRank: vessel.fleetRank ?? 0,
        // machinery: vessel.performance?.scores?.machinery ?? 0,
        type: vessel.status?.reportType || 'Noon Report',
        received: vessel.status?.statusTime
          ? vessel.status.statusTime.split('T')[0]
          : null,
        status: vessel.status?.statusTime
          ? vessel.status.statusTime.split('T')[0]
          : null,
        status: vessel.status ?? {},
        attachmentsCount: vessel.attachments?.length || 0,
        attachments: vessel.attachments || [],
        fleetId: vessel.fleetId,
        fleetName: vessel.fleetName
      };
    });
  }, [vessels]);

  const handleOpenAttachments = (row) => {
    setCurrentAttachments(row.attachments || []);
    setCurrentVesselName(row.name);
    setOpenAttachments(true);
  };

  const handleDownload = async (file) => {
    const response = await downloadVesselFiles(file);
  };

  const handleCloseAttachments = () => {
    setOpenAttachments(false);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredData = useMemo(() => {
    return transformedVessels.filter((v) =>
      v.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, transformedVessels]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const aVal = a[orderBy];
      const bVal = b[orderBy];
      return order === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  }, [filteredData, orderBy, order]);

  const visibleRows = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box
      sx={{
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        background: 'linear-gradient(to top right, #ffffff, #d9ebff)',
        border: '1px solid rgba(39, 174, 227, 0.54)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        mb: 4,
      }}
    >
      <Typography
        variant="h6"
        fontWeight={400}
        color="white"
        mb={2}
        sx={{
          fontSize: 18,
          background: 'linear-gradient(to right, #1a325d, #27ade3)',
          p: 2,
          paddingBottom: 3,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        Fleet <Box component="span" fontWeight={700}>Overview</Box>
      </Typography>

      {openAttachments && (
        <div className="relative z-50">
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded bg-white p-6 shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0a2540]">
                  Attachments for <span className="text-[#27ade3]">{currentVesselName}</span>
                </h3>
              </div>

              {/* Attachments List */}
              <div className="space-y-2">
                {currentAttachments.length > 0 ? (
                  <div className="space-y-3">
                    {currentAttachments.map((file) => (
                      <a
                        key={file.id}
                        onClick={() => handleDownload(file)}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                            {file.name.endsWith('.pdf') ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                            ) : file.name.endsWith('.docx') ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            ) : file.name.endsWith('.xlsx') ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800 truncate max-w-[180px]">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.name.split('.').pop().toUpperCase()} file
                            </p>
                          </div>
                        </div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-400 hover:text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-gray-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                      />
                    </svg>
                    <p className="text-sm text-gray-500">No attachments available</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseAttachments}
                  className="px-4 py-2 bg-[#0a2540] text-white rounded hover:bg-[#27ade3] text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          background: 'none',
          mx: 3,
          marginTop: -4,
        }}
      >
        <TableContainer sx={{ background: 'transparent' }}>
          <Table size="small" sx={{ background: 'transparent' }}>
            <TableHead
              sx={{
                background: '#8ebdde',
                textTransform: 'capitalize',
              }}
            >
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id}>
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleSort(headCell.id)}
                      hideSortIcon={false}
                      IconComponent={() => (
                        <SortIcon direction={order} active={orderBy === headCell.id} />
                      )}
                    >
                      <Typography fontWeight="500" fontSize={16} color="white" sx={{
                        py: 1
                      }}>
                        {headCell.label}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleRows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    background: 'transparent',
                    '&:hover': {
                      background: 'linear-gradient(to top right, #f1f5f9, #d9ebff)',
                    },
                    '& td, & th': { borderBottom: 'none' },
                    borderBottom: "solid 1px #dbdee1"
                  }}
                >
                  <TableCell sx={{ py: 1 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <img src={shipIcon} alt="Vessel" style={{ width: 25, height: 16 }} />
                      <Link
                        component="a"
                        underline="hover"
                        fontWeight={400}
                        sx={{ color: '#777777', fontSize: 16, cursor: 'pointer' }}
                        onClick={(e) => {
                          e.preventDefault();
                          dispatch(setCurrentVessel({ _id: row.id, name: row.name }));
                          navigate('/vessel-overview');
                        }}
                      >
                        {row.name}
                      </Link>
                    </Box>
                  </TableCell>
                 
                  <TableCell sx={{ py: 1 }}><ColouredScore score={row.emission} /></TableCell>
                  <TableCell sx={{ py: 1 }}><ColouredScore score={row.performance} /></TableCell>
                   <TableCell sx={{ py: 1 }}>
                    <ColouredScore score={row.score} width={60} height={60} />
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {row.fleetRank}
                  </TableCell>
                  {/* <TableCell sx={{ py: 1 }}><ColouredScore score={row.machinery} /></TableCell> */}
                  <TableCell sx={{ py: 1 }}>{row.type}</TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Typography
                      fontSize={16}
                      fontWeight={500}
                      sx={{
                        color: getReceivedColor(row.received),
                        borderRadius: 1,
                        display: 'inline-block',
                      }}
                    >
                      {row.received}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        scale: 1.1
                      }
                    }}
                    style={{ display: 'flex', justifyContent: 'center', height: '70px' }}
                    onClick={() => navigate("/vessel/vessel-data-quality", {
                      state: {
                        fleetId: row.fleetId,
                        fleetName: row.fleetName,
                        currentVesselId: row.id
                      }
                    })}
                  >
                    {(getDynamicStatus(row.received))}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <Link
                      component="button"
                      onClick={() => handleOpenAttachments(row)}
                      sx={{
                        width: '100%',
                        gap: 0.5,
                        textDecoration: 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          width: '35px',
                          margin: 'auto',
                          px: 1.5,
                          py: 0.5,
                          fontSize: 14,
                          fontWeight: 600,
                          borderRadius: '20px',
                          bgcolor: 'rgba(25, 118, 210, 0.08)',
                          color: 'primary.main',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'primary.main',
                            color: 'common.white',
                          },
                        }}
                      >{row.attachmentsCount}</Typography>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={sortedData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #e2e8f0',
            '& .MuiTablePagination-displayedRows': {
              fontSize: 12,
              color: '#64748b',
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: 12,
              color: '#64748b',
            },
            '& .MuiTablePagination-select': {
              fontSize: 12,
            },
          }}
        />
      </Paper>
    </Box>
  )
};

export default OverviewTable;
